/**
 * 文本图层命令（M2-18）。
 * 改字/改字号/改字体都是「换 runs/box」的结构性命令；像素重栅格化
 * 由调用方在命令之后经像素事务完成（一次编辑 = 结构条目 + 像素条目，
 * 两步 undo；合并为单条目待历史 API 支持复合提交后收敛）。
 */
import type { Command, Json } from '../command-bus';
import { findLayer, layerDefaults } from '../../document/draft';
import type { LayerId, Rect, TextLayer, TextRun } from '../../document/types';

type TextWarp = NonNullable<TextLayer['warp']>;

const STRUCTURAL_COST = 512;

export function createTextLayer(
  id: LayerId,
  name: string,
  runs: readonly TextRun[],
  box: Rect,
): TextLayer {
  return {
    ...layerDefaults(id, name),
    kind: 'text',
    runs,
    box,
    writingMode: 'horizontal',
  };
}

/** 修改文本内容/样式（runs 与 box 一并替换）。 */
export function createSetTextCommand(
  id: LayerId,
  runs: readonly TextRun[],
  box: Rect,
): Command {
  return {
    kind: 'text.set',
    label: '编辑文本',
    cost: STRUCTURAL_COST,
    // `TextRun`/`Rect` 没有索引签名，但字段全是纯数据；转换是精确类型，不是 any
    record: { kind: 'text.set', args: { id, runs, box } as unknown as Readonly<Record<string, Json>> },
    apply(draft) {
      draft.updateLayer(id, (layer) => (layer.kind === 'text' ? { ...layer, runs, box } : layer));
    },
    invert(document) {
      const layer = findLayer(document.root, id)?.layer;
      if (layer?.kind !== 'text') return null;
      return createSetTextCommand(id, layer.runs, layer.box);
    },
  };
}

/** 切换横排/竖排（M3-16）。结构命令：只改 writingMode，栅格化结果由调用方重新生成。 */
export function createSetWritingModeCommand(
  id: LayerId,
  writingMode: 'horizontal' | 'vertical',
): Command {
  return {
    kind: 'text.writingMode',
    label: writingMode === 'vertical' ? '切换为竖排' : '切换为横排',
    cost: STRUCTURAL_COST,
    record: { kind: 'text.writingMode', args: { id, writingMode } },
    apply(draft) {
      draft.updateLayer(id, (layer) => (layer.kind === 'text' ? { ...layer, writingMode } : layer));
    },
    invert(document) {
      const layer = findLayer(document.root, id)?.layer;
      if (layer?.kind !== 'text') return null;
      return createSetWritingModeCommand(id, layer.writingMode);
    },
  };
}

/** 设置文字变形（阶段 5）。跟 `mask.ts` 的 add/remove 拆成两条命令同一惯例——
 * `exactOptionalPropertyTypes` 下"设成 undefined"跟"没有这个键"不是同一件事，
 * 清除变形走 {@link createRemoveTextWarpCommand}，不是拿 undefined 调这条。 */
export function createSetTextWarpCommand(id: LayerId, warp: TextWarp): Command {
  return {
    kind: 'text.warp.set',
    label: '文字变形',
    cost: STRUCTURAL_COST,
    record: { kind: 'text.warp.set', args: { id, warp } as unknown as Readonly<Record<string, Json>> },
    apply(draft) {
      draft.updateLayer(id, (layer) => (layer.kind === 'text' ? { ...layer, warp } : layer));
    },
    invert(document) {
      const layer = findLayer(document.root, id)?.layer;
      if (layer?.kind !== 'text') return null;
      return layer.warp === undefined
        ? createRemoveTextWarpCommand(id)
        : createSetTextWarpCommand(id, layer.warp);
    },
  };
}

/** 清除文字变形，回到平铺文本。 */
export function createRemoveTextWarpCommand(id: LayerId): Command {
  return {
    kind: 'text.warp.remove',
    label: '清除文字变形',
    cost: STRUCTURAL_COST,
    record: { kind: 'text.warp.remove', args: { id } },
    apply(draft) {
      draft.updateLayer(id, (layer) => {
        if (layer.kind !== 'text') return layer;
        const { warp, ...rest } = layer;
        void warp;
        return rest as typeof layer;
      });
    },
    invert(document) {
      const layer = findLayer(document.root, id)?.layer;
      if (layer?.kind !== 'text') return null;
      return layer.warp === undefined
        ? createRemoveTextWarpCommand(id)
        : createSetTextWarpCommand(id, layer.warp);
    },
  };
}
